/**
 * The worklist's problem-chip row: one count chip per problem type present in
 * the run, ordered severity-first then count. Every chip — expected-state
 * greens included — scrolls to and expands its standing worklist group (the
 * greens sit collapsed at the bottom as reference groups; Ian's ruling
 * 2026-08-11 superseded the mockup's chips-only greens).
 * Visual spec: the approved audit-remodel mockup artifact `.chiprow`/`.chip`.
 *
 * Since 2026-09-28 the row is no longer always on screen: it renders inside
 * SeverityChipsPanel, one severity at a time, when its triage tile is opened.
 * The panel owns the spacing around it.
 */
import React from "react"

import { ProblemSummaryEntry } from "../../../api/audit"
import { THEME } from "../../../config/theme"
import { ProblemTypeMap } from "../../../hooks/useProblemTypes"

interface Props {
  entries: ProblemSummaryEntry[]
  registry: ProblemTypeMap | null
  onChipClick: (slug: string) => void
}

const SEVERITY_ORDER: Record<string, number> = {
  critical: 0,
  warning: 1,
  cleanup: 2,
  expected: 3,
}

const SEVERITY_COLORS = THEME.problemDisplay.severityColors as Record<string, string>

/**
 * The standing green groups read in a fixed operator order (Ian, 2026-08-12),
 * not by count — the sequence is how the lot actually flows, so it must not
 * reshuffle itself as the numbers move day to day. Any expected slug not listed
 * (today only the empty `unclassified-draft-special`) falls in after these,
 * still by count. Problem groups are untouched: none of them appears here, so
 * both sides tie and the comparator falls through to count as before.
 */
const EXPECTED_SLUG_ORDER = ["completed", "pending", "incoming-pipeline", "dolan-assets"]

function fixedGreenRank(slug: string): number {
  const index = EXPECTED_SLUG_ORDER.indexOf(slug)
  return index === -1 ? Number.MAX_SAFE_INTEGER : index
}

export function orderBySeverityThenCount(
  entries: ProblemSummaryEntry[],
  registry: ProblemTypeMap | null,
): ProblemSummaryEntry[] {
  return [...entries].sort((a, b) => {
    const severityA = SEVERITY_ORDER[registry?.[a.problem_type]?.severity ?? "cleanup"] ?? 2
    const severityB = SEVERITY_ORDER[registry?.[b.problem_type]?.severity ?? "cleanup"] ?? 2
    if (severityA !== severityB) return severityA - severityB
    const rankA = fixedGreenRank(a.problem_type)
    const rankB = fixedGreenRank(b.problem_type)
    if (rankA !== rankB) return rankA - rankB
    return b.count - a.count
  })
}

export default function ProblemChipsRow({ entries, registry, onChipClick }: Props) {
  const ordered = orderBySeverityThenCount(entries, registry)

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {ordered.map((entry) => {
        const info = registry?.[entry.problem_type]
        const severity = info?.severity ?? "cleanup"
        const accent = SEVERITY_COLORS[severity] ?? THEME.colors.semantic.info
        return (
          <button
            key={entry.problem_type}
            type="button"
            onClick={() => onChipClick(entry.problem_type)}
            title={info ? info.meaning : entry.problem_type}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: THEME.colors.white,
              border: `1px solid ${THEME.colors.neutral.light.border}`,
              borderRadius: THEME.borderRadius.sm,
              padding: "4px 10px 4px 8px",
              fontSize: 12.5,
              fontWeight: 500,
              fontFamily: THEME.fonts.primary,
              color: THEME.colors.neutral.light.textPrimary,
              whiteSpace: "nowrap",
              cursor: "pointer",
            }}
          >
            <span
              style={{
                width: THEME.problemDisplay.chipAccentBarWidth,
                height: 14,
                borderRadius: 2,
                flex: "none",
                background: accent,
              }}
            />
            {info?.label ?? entry.problem_type}
            <span
              style={{
                color: THEME.colors.neutral.light.textSecondary,
                fontWeight: 600,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {entry.count.toLocaleString()}
            </span>
          </button>
        )
      })}
    </div>
  )
}
